import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getPlatformOverview } from '../../utils/superAdminApi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaUtensils, 
  FaUsers,
  FaRupeeSign,
  FaShoppingCart,
  FaPlus,
  FaEye
} from 'react-icons/fa';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Bar,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const value = data.name === 'Revenue' 
      ? `₹${data.value.toLocaleString()}`
      : data.value;
      
    return (
      <div className="p-3 bg-gray-700 border border-gray-600 rounded-lg shadow-lg">
        <p className="label text-white font-bold">{`${label}`}</p>
        <p className="intro" style={{ color: data.stroke || data.fill }}>{`${data.name}: ${value}`}</p>
      </div>
    );
  }
  return null;
};

export default function SuperAdminOverview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { superAdmin } = useSuperAdmin();
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    // Only fetch if both dates are set, or both are null (reset)
    if ((startDate && endDate) || (!startDate && !endDate)) {
      fetchOverview();
    }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      let params = {};
      console.log('[SuperAdminOverview] startDate:', startDate, 'endDate:', endDate);
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.startDate = start.toISOString();
        params.endDate = end.toISOString();
      }
      console.log('[SuperAdminOverview] Fetching overview with params:', params);
      const data = await getPlatformOverview(params);
      console.log('[SuperAdminOverview] API response:', data);
      setOverview(data.overview);
    } catch (err) {
      setError('Failed to load platform overview');
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };


  const statsCards = [
    {
      title: 'Total Restaurants',
      value: overview?.totalRestaurants || 0,
      change: overview?.newRegistrations || 0,
      changeText: 'new today',
      icon: <FaUtensils />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Restaurants',
      value: overview?.activeRestaurants || 0,
      changeText: 'in last 24h',
      icon: <FaUsers />,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Total Orders',
      value: overview?.totalOrders || 0,
      change: overview?.todayOrders || 0,
      changeText: 'today',
      icon: <FaShoppingCart />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Total Revenue',
      value: `${(overview?.totalRevenue || 0).toLocaleString()}`,
      change: overview?.todayRevenue || 0,
      changeText: 'today',
      icon: <FaRupeeSign />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  if (loading) {
     return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  if (error) {
     return (
        <div className="bg-gray-800 p-6 rounded-2xl shadow text-center">
            <div className="text-red-400 mb-2">{error}</div>
            <button
              onClick={fetchOverview}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-full text-sm font-medium"
            >
              Try Again
            </button>
        </div>
    );
  }

  return (
    <>
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">
          Welcome back, {superAdmin.name}! 👋
        </h2>
        <p className="text-gray-400">
          Here's what's happening with your DineEase platform today.
        </p>
      </div>
      {/* Date Range Picker */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 mb-6">
        <div className="flex items-center gap-2">
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={update => setDateRange(update)}
            dateFormat="MMM d, yyyy"
            className="px-3 py-1 rounded bg-gray-700 text-white border border-gray-600"
            isClearable={true}
            placeholderText="Select date range"
            maxDate={new Date()}
            calendarClassName="shadow-lg rounded-lg border-0"
            popperClassName="shadow-xl"
            popperPlacement="bottom-end"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
          <button
            className="ml-2 px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-500"
            onClick={() => setDateRange([null, null])}
            disabled={!startDate && !endDate}
          >
            All Time
          </button>
        </div>
      </div> 


      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">{stat.title}</p>
                <p className="text-3xl font-bold text-white mt-1">{stat.value}</p>
                {stat.change !== undefined ? (
                  <p className="text-xs text-green-400 mt-1">
                    +{stat.change} {stat.changeText}
                  </p>
                ) : stat.changeText ? (
                   <p className="text-xs text-gray-400 mt-1">{stat.changeText}</p>
                ) : null}
              </div>
              <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <div className={`${stat.color} text-xl`}>{stat.icon}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"
        >
          <h3 className="text-xl font-semibold text-white mb-4">Revenue Analytics</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={overview?.revenueByMonth} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                <XAxis dataKey="month" stroke="#A0AEC0" />
                <YAxis stroke="#A0AEC0" tickFormatter={(value) => `₹${value/1000}k`}/>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"
        >
          <h3 className="text-xl font-semibold text-white mb-4">New Restaurant Registrations</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={overview?.registrationsByMonth} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                <XAxis dataKey="month" stroke="#A0AEC0" />
                <YAxis stroke="#A0AEC0" allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="count" name="New Restaurants" fill="#34D399" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Recent Activity</h3>
        <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <FaPlus className="text-white text-sm" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {overview?.newRegistrations || 0} new restaurant registrations today
                  </p>
                  <p className="text-sm text-gray-400">Platform growth</p>
                </div>
              </div>
              <FaEye className="text-gray-500" />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <FaShoppingCart className="text-white text-sm" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {overview?.todayOrders || 0} orders placed today
                  </p>
                  <p className="text-sm text-gray-400">Total revenue: ₹{(overview?.todayRevenue || 0).toLocaleString()}</p>
                </div>
              </div>
              <FaEye className="text-gray-500" />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <FaUsers className="text-white text-sm" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {overview?.activeRestaurants || 0} active restaurants
                  </p>
                  <p className="text-sm text-gray-400">{overview?.suspendedRestaurants || 0} suspended</p>
                </div>
              </div>
              <FaEye className="text-gray-500" />
            </div>
          </div>
      </div>
    </>
  );
}